import type { DriveStep } from 'driver.js';

export interface WalkthroughTour {
  id: string;
  steps: DriveStep[];
}

export const ROUTE_TOUR_MAP: { pattern: RegExp; tourId: string }[] = [
  { pattern: /\/moni\/clients\/nouveau/, tourId: 'customers-create' },
  { pattern: /\/moni\/clients\/\d+/, tourId: 'customers-detail' },
  { pattern: /\/moni\/clients/, tourId: 'customers' },
  { pattern: /\/moni\/companies\/nouveau/, tourId: 'companies-create' },
  { pattern: /\/moni\/companies\/\d+/, tourId: 'companies-detail' },
  { pattern: /\/moni\/companies/, tourId: 'companies' },
  { pattern: /\/moni\/fournisseurs\/nouveau/, tourId: 'suppliers-create' },
  { pattern: /\/moni\/fournisseurs\/\d+/, tourId: 'suppliers-detail' },
  { pattern: /\/moni\/fournisseurs/, tourId: 'suppliers' },
  { pattern: /\/moni\/produits\/nouveau/, tourId: 'products-create' },
  { pattern: /\/moni\/produits\/\d+/, tourId: 'products-detail' },
  { pattern: /\/moni\/produits/, tourId: 'products' },
  { pattern: /\/moni\/categories\/nouveau/, tourId: 'categories-create' },
  { pattern: /\/moni\/categories\/\d+/, tourId: 'categories-detail' },
  { pattern: /\/moni\/categories/, tourId: 'categories' },
  { pattern: /\/moni\/reductions\/nouveau/, tourId: 'reductions-create' },
  { pattern: /\/moni\/reductions\/\d+/, tourId: 'reductions-detail' },
  { pattern: /\/moni\/reductions/, tourId: 'reductions' },
  { pattern: /\/moni\/dashboard/, tourId: 'dashboard' },
  { pattern: /\/moni\/ventes\/\d+/, tourId: 'orders-detail' },
  { pattern: /\/moni\/ventes/, tourId: 'orders' },
  { pattern: /\/moni\/factures-recurrentes\/\d+/, tourId: 'recurring-invoices-detail' },
  { pattern: /\/moni\/factures-recurrentes/, tourId: 'recurring-invoices' },
  { pattern: /\/moni\/factures\/\d+/, tourId: 'invoices-detail' },
  { pattern: /\/moni\/factures/, tourId: 'invoices' },
  { pattern: /\/moni\/devis\/\d+/, tourId: 'quotes-detail' },
  { pattern: /\/moni\/devis/, tourId: 'quotes' },
  { pattern: /\/moni\/depenses\/\d+/, tourId: 'depenses-detail' },
  { pattern: /\/moni\/depenses/, tourId: 'depenses' },
  { pattern: /\/moni\/taxes\/\d+/, tourId: 'taxes-detail' },
  { pattern: /\/moni\/taxes/, tourId: 'taxes' },
  { pattern: /\/moni\/utilisateurs\/\d+/, tourId: 'users-detail' },
  { pattern: /\/moni\/utilisateurs/, tourId: 'users' },
  { pattern: /\/moni\/roles\/nouveau/, tourId: 'roles-create' },
  { pattern: /\/moni\/roles\/\d+/, tourId: 'roles-create' },
  { pattern: /\/moni\/roles/, tourId: 'roles' },
  { pattern: /\/moni\/moyens-de-paiement\/\d+/, tourId: 'payment-methods-detail' },
  { pattern: /\/moni\/moyens-de-paiement/, tourId: 'payment-methods' },
  { pattern: /\/moni\/settings/, tourId: 'settings' },
  { pattern: /\/moni\/rapprochement/, tourId: 'rapprochement' },
];

export const WALKTHROUGH_TOURS: Record<string, WalkthroughTour> = {
  'customers-create': {
    id: 'customers-create',
    steps: [
      {
        popover: {
          title: 'Créer un client',
          description: 'Ce formulaire permet d\'enregistrer un nouveau client particulier. Seuls le prénom, le nom et l\'email sont obligatoires.',
        },
      },
      {
        element: '[data-tour="customer-create-form"]',
        popover: {
          title: 'Informations générales',
          description: 'Renseignez ici les coordonnées du client : prénom, nom, email, téléphone, adresse et notes complémentaires.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customer-create-email"]',
        popover: {
          title: 'Email — champ clé',
          description: 'L\'email est indispensable : il sert d\'identifiant de connexion pour le client et permet l\'envoi des devis et factures.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customer-create-btn"]',
        popover: {
          title: 'Valider la création',
          description: 'Une fois le formulaire rempli, cliquez sur <strong>Créer</strong>. Le client reçoit un email d\'invitation pour activer son compte.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Prêt à créer !',
          description: 'Après création, vous serez redirigé vers la fiche du client pour compléter ses informations ou lui créer une commande.',
        },
      },
    ],
  },
  'customers-detail': {
    id: 'customers-detail',
    steps: [
      {
        popover: {
          title: 'Fiche client',
          description: 'Cette page centralise toutes les informations d\'un client : coordonnées, historique commercial, commandes, devis et factures.',
        },
      },
      {
        element: '[data-tour="customer-kpi"]',
        popover: {
          title: 'Indicateurs clés',
          description: '<strong>Total CA</strong> : chiffre d\'affaires généré par ce client. <strong>Solde dû</strong> : montant impayé en cours — s\'affiche en rouge si positif.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customer-profile"]',
        popover: {
          title: 'Carte de contact',
          description: 'Photo, nom, poste et entreprise du client. Cliquez sur le crayon en haut à droite pour modifier ses informations.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customer-contact-actions"]',
        popover: {
          title: 'Actions rapides',
          description: 'Envoyez un email ou appelez directement le client en un clic depuis ces boutons.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="customer-about"]',
        popover: {
          title: 'À propos du contact',
          description: 'Toutes les informations du client : coordonnées, adresse, poste, statut du compte.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customer-right-col"]',
        popover: {
          title: 'Historique commercial',
          description: 'Retrouvez ici l\'entreprise liée, les commandes, devis et factures de ce client — toujours à portée de main.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customer-more-actions"]',
        popover: {
          title: 'Autres actions',
          description: 'Accédez aux actions avancées : envoyer un email, supprimer le client, renvoyer l\'email de vérification…',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez la fiche client !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'companies-create': {
    id: 'companies-create',
    steps: [
      {
        popover: {
          title: 'Créer une entreprise',
          description: 'Ce formulaire crée une nouvelle entreprise et son premier contact client en une seule étape. Seuls le nom et le type d\'entreprise sont obligatoires.',
        },
      },
      {
        element: '[data-tour="company-create-info"]',
        popover: {
          title: 'Informations entreprise',
          description: 'Renseignez le nom, le type, les coordonnées (email, téléphone, site web) et l\'adresse complète de l\'entreprise.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-create-contact"]',
        popover: {
          title: 'Premier contact',
          description: 'Ajoutez simultanément le premier client rattaché à cette entreprise. Ce contact sera créé et lié automatiquement.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-create-btn"]',
        popover: {
          title: 'Valider la création',
          description: 'Cliquez sur <strong>Créer</strong> pour enregistrer l\'entreprise et son premier contact en une seule action.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Prêt à créer !',
          description: 'Après création, vous serez redirigé vers la fiche entreprise pour gérer ses contacts, commandes et factures.',
        },
      },
    ],
  },
  'companies-detail': {
    id: 'companies-detail',
    steps: [
      {
        popover: {
          title: 'Fiche entreprise',
          description: 'Cette page centralise tout sur une entreprise : coordonnées, réseaux sociaux, contacts, commandes, devis et factures.',
        },
      },
      {
        element: '[data-tour="company-kpi"]',
        popover: {
          title: 'Indicateurs clés',
          description: '<strong>Total CA</strong> : chiffre d\'affaires généré par cette entreprise. <strong>Solde dû</strong> : montant impayé en cours — s\'affiche en rouge si positif.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-profile"]',
        popover: {
          title: 'Identité de l\'entreprise',
          description: 'Logo, nom et type de l\'entreprise. Cliquez sur le crayon pour modifier toutes les informations : adresse, numéros légaux, réseaux sociaux.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-contact-actions"]',
        popover: {
          title: 'Actions rapides',
          description: 'Envoyez un email, appelez ou visitez le site web de l\'entreprise directement depuis ces boutons.',
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '[data-tour="company-about"]',
        popover: {
          title: 'À propos',
          description: 'Toutes les coordonnées de l\'entreprise : email, téléphone, adresse, localisation et site web.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-social"]',
        popover: {
          title: 'Réseaux sociaux',
          description: 'Liens vers les pages Facebook, Instagram, LinkedIn, YouTube… Cliquez sur <strong>+</strong> pour les renseigner ou les modifier.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-right-col"]',
        popover: {
          title: 'Historique commercial',
          description: 'Contacts rattachés à l\'entreprise, commandes, devis et factures — tout l\'historique en un coup d\'œil.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="company-more-actions"]',
        popover: {
          title: 'Actions avancées',
          description: 'Modifier, supprimer l\'entreprise ou accéder à d\'autres opérations depuis ce menu déroulant.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez la fiche entreprise !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  customers: {
    id: 'customers',
    steps: [
      {
        popover: {
          title: 'Particuliers',
          description: 'Cette page liste tous vos clients particuliers. Vous pouvez les créer, les rechercher, consulter leurs commandes et revenus.',
        },
      },
      {
        element: '[data-tour="customers-stats"]',
        popover: {
          title: 'Statistiques clients',
          description: 'Suivez en un coup d\'œil le nombre total de clients, les comptes activés et le taux d\'activation des comptes.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customers-new-btn"]',
        popover: {
          title: 'Créer un client',
          description: 'Cliquez ici pour ajouter manuellement un nouveau client particulier avec ses coordonnées.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="customers-import-btn"]',
        popover: {
          title: 'Import massif',
          description: 'Vous avez une liste Excel de clients ? Importez-les en masse via ce bouton pour éviter la saisie manuelle.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="customers-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Utilisez la barre de recherche pour retrouver rapidement un client par nom, email ou téléphone.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="customers-table"]',
        popover: {
          title: 'Liste des clients',
          description: 'Chaque ligne affiche le client, ses coordonnées, le statut de son compte, le nombre de commandes et son chiffre d\'affaires. Cliquez sur l\'œil pour voir le détail.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Particuliers !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  companies: {
    id: 'companies',
    steps: [
      {
        popover: {
          title: 'Entreprises',
          description: 'Cette page regroupe toutes vos entreprises clientes. Associez-les à des particuliers pour centraliser la facturation.',
        },
      },
      {
        element: '[data-tour="companies-stats"]',
        popover: {
          title: 'Statistiques entreprises',
          description: 'Visualisez le nombre total d\'entreprises, celles avec un contact renseigné et celles ayant un site web.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="companies-new-btn"]',
        popover: {
          title: 'Créer une entreprise',
          description: 'Ajoutez une nouvelle entreprise cliente : nom, type, coordonnées, localisation et logo.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="companies-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Retrouvez rapidement une entreprise par son nom depuis la barre de recherche.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="companies-table"]',
        popover: {
          title: 'Liste des entreprises',
          description: 'Chaque ligne affiche le nom, le type d\'entreprise, les contacts et la localisation. Utilisez le crayon pour modifier ou la corbeille pour supprimer.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Entreprises !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  products: {
    id: 'products',
    steps: [
      {
        popover: {
          title: 'Produits',
          description: 'Cette page liste tous vos produits. Vous pouvez les créer, les rechercher, consulter leur stock, leur prix et leur statut de publication.',
        },
      },
      {
        element: '[data-tour="products-new-btn"]',
        popover: {
          title: 'Nouveau produit',
          description: 'Cliquez ici pour créer un nouveau produit : nom, description, prix, stock et images.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="products-import-btn"]',
        popover: {
          title: 'Import massif',
          description: 'Vous avez un catalogue Excel ? Importez vos produits en masse pour éviter la saisie manuelle.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="products-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Utilisez la loupe pour retrouver rapidement un produit par son nom.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="products-table"]',
        popover: {
          title: 'Liste des produits',
          description: 'Chaque ligne affiche le produit, sa catégorie, son statut (actif/inactif), la quantité en stock et les prix HT/TTC. Cliquez sur le crayon pour modifier.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Produits !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'products-create': {
    id: 'products-create',
    steps: [
      {
        popover: {
          title: 'Nouveau produit',
          description: 'Ce formulaire permet de créer un nouveau produit. Seul le nom est obligatoire, mais plus vous renseignez d\'informations, mieux le produit sera présenté à vos clients.',
        },
      },
      {
        element: '[data-tour="product-form-main"]',
        popover: {
          title: 'Informations générales',
          description: 'Saisissez le nom, la référence, le code-barres et la description du produit. La description accepte le formatage riche (gras, listes, liens).',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-price"]',
        popover: {
          title: 'Prix de base',
          description: 'Définissez la taxe applicable, le prix HT et le prix TTC. Les deux champs prix sont synchronisés — modifier l\'un recalcule l\'autre automatiquement.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-price-promo"]',
        popover: {
          title: 'Prix promotionnels',
          description: 'Planifiez des prix réduits sur une période donnée. Cliquez sur <strong>+</strong> pour ajouter une promotion avec sa date de début et de fin. Le prix promotionnel s\'appliquera automatiquement en boutique.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-variations"]',
        popover: {
          title: 'Variations',
          description: 'Créez des déclinaisons du produit (taille, couleur, format…). Chaque variation peut avoir son propre prix, stock et référence.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-form-options"]',
        popover: {
          title: 'Publication & visibilité',
          description: 'Activez le produit, rendez-le disponible en ligne, marquez-le comme nouveauté, coup de cœur ou en promotion.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-form-stock"]',
        popover: {
          title: 'Gestion de stock',
          description: 'Activez la gestion de stock pour suivre les quantités disponibles. Si désactivé, choisissez un statut fixe (en stock, rupture, sur commande).',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-submit-btn"]',
        popover: {
          title: 'Créer le produit',
          description: 'Cliquez sur <strong>Créer</strong> pour enregistrer le produit. Vous pourrez ensuite ajouter des images depuis la fiche produit.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Prêt à créer !',
          description: 'Après création, la page se rechargera pour vous permettre d\'ajouter des images.',
        },
      },
    ],
  },
  'products-detail': {
    id: 'products-detail',
    steps: [
      {
        popover: {
          title: 'Fiche produit',
          description: 'Cette page centralise tout sur un produit : informations, prix, variations, images et options de publication.',
        },
      },
      {
        element: '[data-tour="product-form-main"]',
        popover: {
          title: 'Informations générales',
          description: 'Modifiez le nom, la référence, le code-barres et la description du produit.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-price"]',
        popover: {
          title: 'Prix de base',
          description: 'Ajustez la taxe, le prix HT et le prix TTC. Les deux champs prix sont synchronisés — modifier l\'un recalcule l\'autre automatiquement.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-price-promo"]',
        popover: {
          title: 'Prix promotionnels',
          description: 'Planifiez des prix réduits sur une période donnée. Cliquez sur <strong>+</strong> pour ajouter une promotion avec sa date de début et de fin.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-variations"]',
        popover: {
          title: 'Variations',
          description: 'Gérez les déclinaisons du produit : tailles, couleurs, formats… Chaque variation peut avoir son propre prix, stock et référence.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-form-options"]',
        popover: {
          title: 'Publication & visibilité',
          description: 'Activez le produit, rendez-le disponible en ligne, marquez-le comme nouveauté, coup de cœur ou en promotion.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-form-stock"]',
        popover: {
          title: 'Gestion de stock',
          description: 'Activez la gestion de stock pour suivre les quantités disponibles. Si désactivé, choisissez un statut fixe (en stock, rupture, sur commande).',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-images"]',
        popover: {
          title: 'Images',
          description: 'Ajoutez, réordonnez ou supprimez les photos du produit. La première image est utilisée comme visuel principal en boutique.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="product-submit-btn"]',
        popover: {
          title: 'Enregistrer',
          description: 'Cliquez sur <strong>Enregistrer</strong> pour valider toutes vos modifications.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez la fiche produit !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  suppliers: {
    id: 'suppliers',
    steps: [
      {
        popover: {
          title: 'Fournisseurs',
          description: 'Cette page liste tous vos fournisseurs. Gérez leurs coordonnées, retrouvez-les rapidement et accédez à leur fiche pour les modifier.',
        },
      },
      {
        element: '[data-tour="suppliers-new-btn"]',
        popover: {
          title: 'Nouveau fournisseur',
          description: 'Cliquez ici pour créer un nouveau fournisseur : nom, coordonnées, adresse et numéro fiscal.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="suppliers-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Utilisez la loupe pour retrouver rapidement un fournisseur par son nom.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="suppliers-table"]',
        popover: {
          title: 'Liste des fournisseurs',
          description: 'Chaque ligne affiche le nom, l\'email, le téléphone et la ville du fournisseur. Utilisez le crayon pour modifier ou la corbeille pour supprimer.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Fournisseurs !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'suppliers-create': {
    id: 'suppliers-create',
    steps: [
      {
        popover: {
          title: 'Nouveau fournisseur',
          description: 'Ce formulaire permet d\'enregistrer un nouveau fournisseur. Seul le nom est obligatoire.',
        },
      },
      {
        element: '[data-tour="supplier-form-info"]',
        popover: {
          title: 'Informations',
          description: 'Renseignez le nom, l\'email, le téléphone, le site web et le numéro fiscal ou SIRET du fournisseur.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="supplier-form-address"]',
        popover: {
          title: 'Adresse',
          description: 'Saisissez l\'adresse complète du fournisseur : rue, ville, code postal et pays.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="supplier-submit-btn"]',
        popover: {
          title: 'Créer le fournisseur',
          description: 'Une fois le formulaire rempli, cliquez sur <strong>Créer</strong> pour enregistrer le fournisseur.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Prêt à créer !',
          description: 'Après création, vous serez redirigé vers la liste des fournisseurs.',
        },
      },
    ],
  },
  'suppliers-detail': {
    id: 'suppliers-detail',
    steps: [
      {
        popover: {
          title: 'Modifier un fournisseur',
          description: 'Cette page permet de mettre à jour les informations d\'un fournisseur existant.',
        },
      },
      {
        element: '[data-tour="supplier-form-info"]',
        popover: {
          title: 'Informations',
          description: 'Modifiez le nom, l\'email, le téléphone, le site web et le numéro fiscal du fournisseur.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="supplier-form-address"]',
        popover: {
          title: 'Adresse',
          description: 'Mettez à jour l\'adresse complète : rue, ville, code postal et pays.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="supplier-submit-btn"]',
        popover: {
          title: 'Enregistrer les modifications',
          description: 'Cliquez sur <strong>Enregistrer</strong> pour valider toutes vos modifications.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Modifications enregistrées !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  categories: {
    id: 'categories',
    steps: [
      {
        popover: {
          title: 'Catégories',
          description: 'Cette page liste toutes vos catégories de produits. Organisez votre catalogue en créant une arborescence de catégories et sous-catégories.',
        },
      },
      {
        element: '[data-tour="categories-new-btn"]',
        popover: {
          title: 'Nouvelle catégorie',
          description: 'Cliquez ici pour créer une nouvelle catégorie. Vous pourrez lui associer une image, une couleur et une catégorie parente.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="categories-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Utilisez la loupe pour retrouver rapidement une catégorie par son nom.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="categories-table"]',
        popover: {
          title: 'Liste des catégories',
          description: 'Chaque ligne affiche la catégorie, sa catégorie parente, son statut et son ordre d\'affichage. Activez ou désactivez une catégorie directement depuis la liste.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Catégories !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'categories-create': {
    id: 'categories-create',
    steps: [
      {
        popover: {
          title: 'Nouvelle catégorie',
          description: 'Ce formulaire permet de créer une catégorie pour organiser vos produits. Seul le nom est obligatoire.',
        },
      },
      {
        element: '[data-tour="category-form-info"]',
        popover: {
          title: 'Informations générales',
          description: 'Renseignez le nom, l\'ordre d\'affichage, la couleur, la description et la catégorie parente. Une catégorie parente permet de créer des sous-catégories.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="category-form-media"]',
        popover: {
          title: 'Image',
          description: 'Ajoutez une image pour illustrer la catégorie. Elle sera affichée dans la boutique en ligne.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="category-submit-btn"]',
        popover: {
          title: 'Créer la catégorie',
          description: 'Cliquez sur <strong>Créer</strong> pour enregistrer la catégorie. Elle sera immédiatement disponible pour être associée à vos produits.',
          side: 'top',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Prêt à créer !',
          description: 'Après création, vous serez redirigé vers la liste des catégories.',
        },
      },
    ],
  },
  'categories-detail': {
    id: 'categories-detail',
    steps: [
      {
        popover: {
          title: 'Modifier une catégorie',
          description: 'Cette page permet de mettre à jour les informations d\'une catégorie existante.',
        },
      },
      {
        element: '[data-tour="category-form-info"]',
        popover: {
          title: 'Informations générales',
          description: 'Modifiez le nom, l\'ordre d\'affichage, la couleur, la description et la catégorie parente.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="category-form-media"]',
        popover: {
          title: 'Image',
          description: 'Remplacez l\'image de la catégorie en sélectionnant un nouveau fichier.',
          side: 'left',
          align: 'start',
        },
      },
      {
        element: '[data-tour="category-submit-btn"]',
        popover: {
          title: 'Enregistrer les modifications',
          description: 'Cliquez sur <strong>Enregistrer</strong> pour valider toutes vos modifications.',
          side: 'top',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Modifications enregistrées !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  reductions: {
    id: 'reductions',
    steps: [
      {
        popover: {
          title: 'Réductions',
          description: 'Cette page liste tous vos codes de réduction. Créez des coupons à montant fixe ou en pourcentage, limités dans le temps ou en quantité.',
        },
      },
      {
        element: '[data-tour="reductions-new-btn"]',
        popover: {
          title: 'Nouvelle réduction',
          description: 'Cliquez ici pour créer un nouveau code coupon avec ses règles d\'utilisation.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="reductions-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Utilisez la loupe pour retrouver rapidement une réduction par son nom.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reductions-table"]',
        popover: {
          title: 'Liste des réductions',
          description: 'Chaque ligne affiche le nom, le code, la valeur, le type (% ou montant fixe), la période de validité, le nombre d\'utilisations restantes et le statut.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Réductions !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'reductions-create': {
    id: 'reductions-create',
    steps: [
      {
        popover: {
          title: 'Nouvelle réduction',
          description: 'Ce formulaire permet de créer un code coupon. Le nom, le code et la valeur sont obligatoires.',
        },
      },
      {
        element: '[data-tour="reduction-form-value"]',
        popover: {
          title: 'Valeur et type',
          description: 'Saisissez la valeur de la réduction. Cliquez sur l\'icône à droite du champ pour basculer entre <strong>montant fixe (XPF)</strong> et <strong>pourcentage (%)</strong>.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-form-period"]',
        popover: {
          title: 'Période de validité',
          description: 'Définissez une date de début et une date de fin pour limiter la durée de validité du coupon. Ces champs sont optionnels — sans dates, le coupon est valide indéfiniment.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-form-usage"]',
        popover: {
          title: 'Quantité et usage',
          description: 'Limitez le nombre d\'utilisations disponibles. Activez <strong>usage unique</strong> pour qu\'un client ne puisse utiliser le code qu\'une seule fois. Laissez vide pour une quantité illimitée.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-submit-btn"]',
        popover: {
          title: 'Créer la réduction',
          description: 'Cliquez sur <strong>Créer</strong> pour enregistrer le code coupon. Il sera immédiatement utilisable par vos clients en boutique.',
          side: 'top',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Prêt à créer !',
          description: 'Après création, vous serez redirigé vers la liste des réductions.',
        },
      },
    ],
  },
  'reductions-detail': {
    id: 'reductions-detail',
    steps: [
      {
        popover: {
          title: 'Modifier une réduction',
          description: 'Cette page permet de modifier les règles d\'un code coupon et de consulter les commandes qui l\'ont utilisé.',
        },
      },
      {
        element: '[data-tour="reduction-form-value"]',
        popover: {
          title: 'Valeur et type',
          description: 'Modifiez la valeur de la réduction et basculez entre montant fixe et pourcentage via l\'icône à droite du champ.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-form-period"]',
        popover: {
          title: 'Période de validité',
          description: 'Ajustez les dates de début et de fin pour prolonger ou restreindre la durée d\'utilisation du coupon.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-form-usage"]',
        popover: {
          title: 'Quantité et usage',
          description: 'Mettez à jour le nombre d\'utilisations disponibles ou l\'option d\'usage unique.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-tabs"]',
        popover: {
          title: 'Commandes associées',
          description: 'Cliquez sur <strong>Commandes associées</strong> pour voir la liste de toutes les commandes qui ont utilisé ce code coupon, avec le montant de réduction appliqué pour chacune.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="reduction-submit-btn"]',
        popover: {
          title: 'Mettre à jour',
          description: 'Cliquez sur <strong>Mettre à jour</strong> pour enregistrer vos modifications.',
          side: 'top',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Réductions !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  dashboard: {
    id: 'dashboard',
    steps: [
      {
        popover: {
          title: 'Tableau de bord',
          description: 'Le tableau de bord vous donne une vue d\'ensemble de votre activité : chiffre d\'affaires, commandes récentes, nouveaux clients et produits les plus vendus.',
        },
      },
      {
        element: '[data-tour="dashboard-kpi"]',
        popover: {
          title: 'Indicateurs clés',
          description: 'Ces cartes affichent vos principaux chiffres du moment : CA du mois, nombre de commandes, montant moyen des commandes et factures en attente.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="dashboard-chart"]',
        popover: {
          title: 'Graphique de performance',
          description: 'Ce graphique illustre l\'évolution de vos ventes sur la période. Cliquez sur <strong>Changer de vue</strong> pour alterner entre le chiffre d\'affaires et le nombre de commandes.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="dashboard-orders"]',
        popover: {
          title: 'Dernières commandes',
          description: 'Retrouvez ici les commandes les plus récentes avec leur statut, le client et le montant. Cliquez sur <strong>Voir</strong> pour accéder au détail de la commande.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="dashboard-customers"]',
        popover: {
          title: 'Nouveaux clients',
          description: 'Liste des derniers clients inscrits. L\'indicateur de statut vous indique si le compte a été vérifié par email.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez le tableau de bord !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  orders: {
    id: 'orders',
    steps: [
      {
        popover: {
          title: 'Commandes',
          description: 'Cette page liste toutes vos commandes clients. Vous pouvez filtrer par statut, suivre les paiements et gérer chaque commande individuellement.',
        },
      },
      {
        element: '[data-tour="orders-stats"]',
        popover: {
          title: 'Statistiques commandes',
          description: 'Visualisez le nombre total de commandes, le montant total, les commandes en attente de paiement et les commandes confirmées.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="orders-new-btn"]',
        popover: {
          title: 'Nouvelle commande',
          description: 'Cliquez ici pour créer manuellement une commande pour un client (utile pour les commandes passées en magasin ou par téléphone).',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="orders-toolbar"]',
        popover: {
          title: 'Filtres par statut',
          description: 'Filtrez les commandes par statut (Nouveau, En cours, Livré…) grâce aux segments de filtre. Utilisez aussi la loupe pour rechercher une commande spécifique.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="orders-table"]',
        popover: {
          title: 'Liste des commandes',
          description: 'Chaque ligne affiche le numéro de commande, la date, le statut, le client, le total et le statut de paiement. Cliquez sur l\'œil pour voir le détail.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Commandes !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'orders-detail': {
    id: 'orders-detail',
    steps: [
      {
        popover: {
          title: 'Détail de commande',
          description: 'Cette page centralise toutes les informations d\'une commande : statut, produits, paiements, adresses de facturation et de livraison.',
        },
      },
      {
        element: '[data-tour="order-status"]',
        popover: {
          title: 'Statut de la commande',
          description: 'Modifiez le statut de la commande (Nouveau, En préparation, Livré…) via le bouton de statut en haut à droite de cette carte.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="order-products"]',
        popover: {
          title: 'Produits commandés',
          description: 'Liste de tous les articles de la commande avec les quantités, prix unitaires et totaux par ligne. Sur une commande en brouillon, vous pouvez ajouter ou retirer des produits.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="order-payments"]',
        popover: {
          title: 'Paiements',
          description: 'Suivez l\'historique des paiements de cette commande. Cliquez sur <strong>Ajouter un paiement</strong> pour enregistrer un encaissement.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="order-totals"]',
        popover: {
          title: 'Totaux',
          description: 'Récapitulatif financier : sous-total, livraison, réduction, taxes et total TTC.',
          side: 'left',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez le détail de commande !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  invoices: {
    id: 'invoices',
    steps: [
      {
        popover: {
          title: 'Factures',
          description: 'Cette page liste toutes vos factures. Créez, suivez et gérez vos factures clients avec leurs statuts de paiement.',
        },
      },
      {
        element: '[data-tour="invoices-kpi"]',
        popover: {
          title: 'Indicateurs clés',
          description: 'Vue d\'ensemble financière : CA facturé, montant en attente, montant payé et factures en retard.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="invoices-new-btn"]',
        popover: {
          title: 'Nouvelle facture',
          description: 'Cliquez ici pour créer manuellement une facture. Les factures sont généralement créées depuis un devis accepté.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="invoices-toolbar"]',
        popover: {
          title: 'Filtres par statut',
          description: 'Filtrez les factures par statut (Brouillon, Envoyée, Payée…) grâce aux segments. La loupe permet de rechercher une facture par référence ou client.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="invoices-table"]',
        popover: {
          title: 'Liste des factures',
          description: 'Chaque ligne affiche la référence, le client, le montant, la date de création et l\'échéance. Cliquez sur l\'œil pour voir le détail.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Factures !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'invoices-detail': {
    id: 'invoices-detail',
    steps: [
      {
        popover: {
          title: 'Détail de facture',
          description: 'Cette page affiche le contenu complet d\'une facture : statut, lignes de facturation, taxes et conditions de paiement.',
        },
      },
      {
        element: '[data-tour="invoice-timeline"]',
        popover: {
          title: 'Cycle de vie',
          description: 'Cette barre de progression montre les étapes de la facture : Brouillon → Envoyée → Payée. L\'étape active est mise en surbrillance.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="invoice-lines"]',
        popover: {
          title: 'Lignes de facturation',
          description: 'Détail de chaque ligne : désignation, quantité, prix unitaire, taxe, remise et total HT. Sur une facture en brouillon, vous pouvez ajouter des lignes de produits ou des lignes libres.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="invoice-totals"]',
        popover: {
          title: 'Totaux et taxes',
          description: 'Tableau de ventilation des taxes et récapitulatif financier : total HT, réduction, total taxes et total TTC.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Factures !',
          description: 'Utilisez les boutons d\'action en haut (Envoyer, Marquer comme payée, Actions) pour gérer le cycle de vie de la facture.',
        },
      },
    ],
  },
  quotes: {
    id: 'quotes',
    steps: [
      {
        popover: {
          title: 'Devis',
          description: 'Cette page liste tous vos devis. Créez, envoyez et transformez vos devis en factures depuis cet écran.',
        },
      },
      {
        element: '[data-tour="quotes-kpi"]',
        popover: {
          title: 'Indicateurs clés',
          description: 'Vue d\'ensemble : montant total des devis, devis en attente de réponse, devis acceptés et devis refusés.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="quotes-new-btn"]',
        popover: {
          title: 'Nouveau devis',
          description: 'Cliquez ici pour créer un nouveau devis. Sélectionnez un client, ajoutez des lignes de produits et envoyez-le directement par email.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="quotes-toolbar"]',
        popover: {
          title: 'Filtres par statut',
          description: 'Filtrez les devis par statut (Brouillon, Envoyé, Accepté, Refusé…). Utilisez la loupe pour rechercher un devis par référence ou client.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="quotes-table"]',
        popover: {
          title: 'Liste des devis',
          description: 'Chaque ligne affiche la référence, le client, le montant, la date de création et la date de validité. Cliquez sur l\'œil pour voir le détail.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Devis !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'quotes-detail': {
    id: 'quotes-detail',
    steps: [
      {
        popover: {
          title: 'Détail de devis',
          description: 'Cette page affiche le contenu complet d\'un devis : statut, lignes de facturation et actions disponibles selon l\'étape.',
        },
      },
      {
        element: '[data-tour="quote-timeline"]',
        popover: {
          title: 'Cycle de vie',
          description: 'Cette barre montre les étapes du devis : Brouillon → Envoyé → Accepté / Refusé. Utilisez les boutons d\'action en haut pour faire avancer le devis.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Actions clés',
          description: 'Sur un devis envoyé, vous pouvez l\'<strong>Accepter</strong>, le <strong>Refuser</strong> ou le <strong>Convertir en facture</strong> en un clic depuis les boutons en haut de la page.',
        },
      },
    ],
  },
  depenses: {
    id: 'depenses',
    steps: [
      {
        popover: {
          title: 'Dépenses',
          description: 'Cette page centralise toutes vos dépenses fournisseurs. Importez des justificatifs (PDF/image) via l\'OCR ou saisissez manuellement vos factures d\'achat.',
        },
      },
      {
        element: '[data-tour="depenses-import-btn"]',
        popover: {
          title: 'Importer via OCR',
          description: 'Glissez ou uploadez un PDF ou une photo de facture. L\'OCR extrait automatiquement le fournisseur, le montant et la date pour vous éviter la saisie manuelle.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="depenses-new-btn"]',
        popover: {
          title: 'Saisie manuelle',
          description: 'Créez une dépense manuellement si vous n\'avez pas de justificatif à uploader.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="depenses-toolbar"]',
        popover: {
          title: 'Filtres',
          description: 'Filtrez les dépenses par catégorie (alimentation, transport, logiciel…) ou par statut de paiement. La loupe permet de rechercher par numéro de facture ou fournisseur.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="depenses-table"]',
        popover: {
          title: 'Liste des dépenses',
          description: 'Chaque ligne affiche le numéro de facture, le fournisseur, la catégorie, le montant TTC, la date, le statut de paiement et l\'indicateur OCR.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Dépenses !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'depenses-detail': {
    id: 'depenses-detail',
    steps: [
      {
        popover: {
          title: 'Détail de dépense',
          description: 'Ce formulaire permet de saisir ou modifier une dépense fournisseur avec tous ses détails comptables.',
        },
      },
      {
        element: '[data-tour="depense-form-main"]',
        popover: {
          title: 'Informations de la dépense',
          description: 'Renseignez le numéro de facture, la date, le fournisseur, la désignation, les montants HT/TTC et la catégorie de dépense.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="depense-save-btn"]',
        popover: {
          title: 'Enregistrer',
          description: 'Cliquez sur <strong>Enregistrer</strong> pour valider la dépense. Elle sera comptabilisée dans vos charges.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Dépenses !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  taxes: {
    id: 'taxes',
    steps: [
      {
        popover: {
          title: 'Taxes',
          description: 'Cette page liste toutes vos taxes (TVA…). Les taxes sont appliquées à vos produits pour calculer les prix TTC et les lignes de facturation.',
        },
      },
      {
        element: '[data-tour="taxes-new-btn"]',
        popover: {
          title: 'Nouvelle taxe',
          description: 'Cliquez ici pour créer une nouvelle taxe avec son nom, son taux en pourcentage et son code.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="taxes-toolbar"]',
        popover: {
          title: 'Recherche',
          description: 'Utilisez la loupe pour retrouver rapidement une taxe par son nom.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="taxes-table"]',
        popover: {
          title: 'Liste des taxes',
          description: 'Chaque ligne affiche le nom de la taxe et son taux. Cliquez sur le crayon pour modifier ou la corbeille pour supprimer.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Taxes !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'taxes-detail': {
    id: 'taxes-detail',
    steps: [
      {
        popover: {
          title: 'Créer / Modifier une taxe',
          description: 'Ce formulaire permet de définir une taxe applicable à vos produits et documents.',
        },
      },
      {
        element: '[data-tour="tax-form"]',
        popover: {
          title: 'Paramètres de la taxe',
          description: 'Renseignez le nom affiché, le taux en pourcentage, un slug (identifiant unique) et une description optionnelle.',
          side: 'right',
          align: 'start',
        },
      },
      {
        element: '[data-tour="tax-submit-btn"]',
        popover: {
          title: 'Enregistrer',
          description: 'Cliquez sur <strong>Créer</strong> ou <strong>Enregistrer</strong> pour valider. La taxe sera immédiatement disponible pour vos produits.',
          side: 'top',
          align: 'end',
        },
      },
    ],
  },
  users: {
    id: 'users',
    steps: [
      {
        popover: {
          title: 'Utilisateurs',
          description: 'Cette page liste tous les administrateurs et collaborateurs ayant accès à l\'application. Gérez leurs rôles et permissions depuis cet écran.',
        },
      },
      {
        element: '[data-tour="users-new-btn"]',
        popover: {
          title: 'Nouvel utilisateur',
          description: 'Cliquez ici pour inviter un nouveau collaborateur. Il recevra un email avec ses informations de connexion.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="users-table"]',
        popover: {
          title: 'Liste des utilisateurs',
          description: 'Chaque ligne affiche le nom, l\'email, le rôle attribué et le statut du compte. Cliquez sur l\'œil pour modifier les informations ou les rôles d\'un utilisateur.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Utilisateurs !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'users-detail': {
    id: 'users-detail',
    steps: [
      {
        popover: {
          title: 'Fiche utilisateur',
          description: 'Cette page permet de modifier les informations d\'un collaborateur : photo, prénom, nom, email, rôles et mot de passe.',
        },
      },
      {
        element: '[data-tour="user-save-btn"]',
        popover: {
          title: 'Enregistrer',
          description: 'Cliquez sur <strong>Enregistrer</strong> pour valider toutes vos modifications.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez la fiche utilisateur !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  roles: {
    id: 'roles',
    steps: [
      {
        popover: {
          title: 'Rôles',
          description: 'Les rôles définissent ce que chaque collaborateur peut faire dans l\'application. Chaque rôle regroupe un ensemble de droits d\'accès.',
        },
      },
      {
        element: '[data-tour="roles-new-btn"]',
        popover: {
          title: 'Nouveau rôle',
          description: 'Cliquez ici pour créer un nouveau rôle personnalisé et lui attribuer les droits que vous souhaitez.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="roles-table"]',
        popover: {
          title: 'Liste des rôles',
          description: 'Chaque ligne affiche le nom du rôle et la liste de ses permissions. Cliquez sur le crayon pour modifier les droits ou la corbeille pour supprimer.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Rôles !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'roles-create': {
    id: 'roles-create',
    steps: [
      {
        popover: {
          title: 'Créer / Modifier un rôle',
          description: 'Ce formulaire permet de définir un rôle et de sélectionner précisément les droits accordés à chaque section de l\'application.',
        },
      },
      {
        element: '[data-tour="role-permissions"]',
        popover: {
          title: 'Matrice des droits',
          description: 'Chaque section du menu est représentée avec ses actions possibles : <strong>Voir</strong>, <strong>Créer</strong>, <strong>Modifier</strong>, <strong>Supprimer</strong>. Cliquez sur les pastilles pour activer ou retirer un droit. Utilisez <strong>Tout cocher</strong> pour accorder tous les droits d\'une section en un clic.',
          side: 'top',
          align: 'start',
        },
      },
      {
        element: '[data-tour="role-submit-btn"]',
        popover: {
          title: 'Enregistrer le rôle',
          description: 'Cliquez sur <strong>Créer</strong> ou <strong>Enregistrer</strong> pour valider les droits du rôle. Les utilisateurs associés à ce rôle verront leurs accès mis à jour immédiatement.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Rôles !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'payment-methods': {
    id: 'payment-methods',
    steps: [
      {
        popover: {
          title: 'Moyens de paiement',
          description: 'Cette page liste les modes de paiement disponibles pour vos clients (espèces, carte, virement, PayZen…). Activez ou désactivez chacun selon vos besoins.',
        },
      },
      {
        element: '[data-tour="payment-methods-table"]',
        popover: {
          title: 'Liste des moyens de paiement',
          description: 'Chaque ligne indique si le mode est actif, disponible en ligne, et son ordre d\'affichage. Les toggles permettent une activation rapide sans ouvrir la fiche.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Moyens de paiement !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'payment-methods-detail': {
    id: 'payment-methods-detail',
    steps: [
      {
        popover: {
          title: 'Configurer un moyen de paiement',
          description: 'Cette page permet de personnaliser un mode de paiement : nom, icône, disponibilité en ligne et paramètres de passerelle si applicable.',
        },
      },
      {
        element: '[data-tour="payment-method-tabs"]',
        popover: {
          title: 'Onglets de configuration',
          description: 'L\'onglet <strong>Informations de base</strong> permet de modifier le nom, l\'icône et les options de disponibilité. L\'onglet <strong>Configuration</strong> contient les paramètres techniques (clés API, URLs de callback…).',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="payment-method-submit-btn"]',
        popover: {
          title: 'Enregistrer',
          description: 'Cliquez sur <strong>Enregistrer</strong> pour valider vos modifications.',
          side: 'bottom',
          align: 'end',
        },
      },
    ],
  },
  'recurring-invoices': {
    id: 'recurring-invoices',
    steps: [
      {
        popover: {
          title: 'Factures récurrentes',
          description: 'Cette page liste vos modèles de factures programmées pour une émission automatique (mensuelle, trimestrielle…). Chaque modèle génère des factures selon la fréquence définie.',
        },
      },
      {
        element: '[data-tour="recurring-new-btn"]',
        popover: {
          title: 'Nouveau modèle',
          description: 'Cliquez ici pour créer un nouveau modèle de récurrence depuis une facture existante.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="recurring-table"]',
        popover: {
          title: 'Liste des récurrences',
          description: 'Chaque ligne affiche le libellé, la fréquence, la facture source, la prochaine exécution, le nombre de factures générées et le statut. Utilisez le toggle pour activer ou suspendre une récurrence.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Factures récurrentes !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  'recurring-invoices-detail': {
    id: 'recurring-invoices-detail',
    steps: [
      {
        popover: {
          title: 'Détail de récurrence',
          description: 'Cette page affiche la configuration d\'une récurrence et liste toutes les factures générées depuis son modèle source.',
        },
      },
      {
        element: '[data-tour="recurring-block"]',
        popover: {
          title: 'Paramètres de récurrence',
          description: 'Ce bloc montre la fréquence d\'émission, les dates de début/fin, le nombre d\'occurrences et les options de récurrence. Modifiez-les selon vos besoins.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="recurring-invoices-table"]',
        popover: {
          title: 'Factures générées',
          description: 'Liste de toutes les factures déjà émises depuis ce modèle de récurrence. Cliquez sur l\'œil pour accéder à une facture spécifique.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Récurrences !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  settings: {
    id: 'settings',
    steps: [
      {
        popover: {
          title: 'Paramètres',
          description: 'Cette page regroupe toutes les configurations de MONICASE : informations de la boutique, apparence, facturation, notifications et bien plus.',
        },
      },
      {
        element: '[data-tour="settings-tabs"]',
        popover: {
          title: 'Groupes de paramètres',
          description: 'Les paramètres sont organisés par catégories (onglets en haut). Cliquez sur chaque onglet pour afficher les réglages correspondants.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="settings-content"]',
        popover: {
          title: 'Réglages',
          description: 'Chaque carte regroupe des paramètres liés. Modifiez les valeurs et n\'oubliez pas d\'enregistrer. Certains paramètres prennent effet immédiatement, d\'autres nécessitent un rechargement.',
          side: 'top',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez les Paramètres !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  rapprochement: {
    id: 'rapprochement',
    steps: [
      {
        popover: {
          title: 'Rapprochement bancaire',
          description: 'Cette page permet de rapprocher vos transactions bancaires avec vos dépenses et factures enregistrées dans MONICASE.',
        },
      },
      {
        element: '[data-tour="rapprochement-stats"]',
        popover: {
          title: 'Statistiques',
          description: 'Vue d\'ensemble du rapprochement : nombre total de transactions, transactions rapprochées, suggestions en attente et transactions non traitées.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="rapprochement-import"]',
        popover: {
          title: 'Importer un relevé',
          description: 'Cliquez ici pour importer un relevé bancaire au format CSV ou OFX. Les transactions sont extraites automatiquement pour être rapprochées.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="rapprochement-automatch"]',
        popover: {
          title: 'Auto-match',
          description: 'Lance le moteur de matching automatique qui tente de faire correspondre chaque transaction avec une dépense ou une facture selon le montant et la date.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        popover: {
          title: 'Vous maîtrisez le Rapprochement !',
          description: 'Retrouvez ce guide à tout moment via le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
  global: {
    id: 'global',
    steps: [
      {
        popover: {
          title: 'Bienvenue sur MONICASE',
          description: 'Ce guide rapide vous présente les grandes sections de l\'application. Cliquez sur <strong>Suivant</strong> pour commencer, ou <strong>Passer</strong> pour le fermer.',
        },
      },
      {
        element: '.layout-topbar',
        popover: {
          title: 'Barre de navigation',
          description: 'Cette barre est toujours visible. Elle vous donne accès à vos boutiques, à toutes les applications MONICASE et à votre profil.',
          side: 'bottom',
          align: 'start',
        },
      },
      {
        element: '[data-tour="app-drawer-btn"]',
        popover: {
          title: 'Toutes vos applications',
          description: 'Cliquez ici pour naviguer entre vos modules : <strong>MONI</strong> (ventes), <strong>SKEDI</strong> (projets), <strong>KRATISI</strong> (réservations), <strong>KLEIDI</strong> (accès)…',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '[data-tour="profile-btn"]',
        popover: {
          title: 'Votre profil',
          description: 'Accédez à vos informations personnelles et déconnectez-vous depuis ce menu.',
          side: 'bottom',
          align: 'end',
        },
      },
      {
        element: '.layout-sidebar',
        popover: {
          title: 'Menu de navigation',
          description: 'Ce menu liste toutes les sections du module actif : commandes, factures, produits, clients, fournisseurs, dépenses…',
          side: 'right',
          align: 'start',
        },
      },
      {
        popover: {
          title: 'Vous êtes prêt !',
          description: 'Vous connaissez maintenant l\'essentiel de MONICASE. Retrouvez ce guide à tout moment en cliquant sur le bouton <strong>?</strong> dans la barre de navigation.',
        },
      },
    ],
  },
};
